import type { HTTPError, Options } from 'ky'
import type { IHttpClient, IHttpRequestSettings } from '../Contracts/Services/IHttpClient'
import { configManager } from '../Managers/Config'
import { userManager } from '../Managers/User'
import { CollectionTransformers, type CollectionTransformer } from './ResourceClientService/CollectionTransformers'
import { requestProfiler } from './RestClientService/RequestProfiler'
import { RequestTransformers, type RequestTransformer } from './RestClientService/RequestTransformers'

/**
 * Базовий абстрактний клас для HTTP клієнтів
 * Містить спільну логіку для всіх HTTP клієнтів у системі
 *
 * @description
 * Цей клас забезпечує:
 * - Управління трансформерами запитів та відповідей
 * - Профілювання HTTP запитів у dev режимі
 * - Управління headers (авторизація, локаль)
 * - Автоматичне оновлення токенів при 401 помилці
 * - Базову обробку опцій запиту
 *
 * @abstract
 * Потрібно імплементувати конкретні HTTP методи у дочірніх класах
 */
export abstract class BaseHttpClient<TSettings extends IHttpRequestSettings = IHttpRequestSettings>
    implements Partial<IHttpClient<TSettings>>
{
    /** URL бекенду за замовчуванням */
    backendUrl: string = ''

    constructor(backendUrl?: string) {
        if (backendUrl) {
            this.backendUrl = backendUrl
        }
    }

    /**
     * Застосовує request трансформер до опцій запиту
     *
     * @param options - базові опції запиту
     * @param settings - налаштування з можливістю вказати трансформер
     * @returns модифіковані опції
     *
     * @description
     * Трансформери використовуються для:
     * - Встановлення правильного Content-Type
     * - Конвертації даних перед відправкою
     * - Додавання кастомних headers
     */
    protected applyRequestTransformer(options: Options = {}, settings: TSettings = {} as TSettings): Options {
        // Якщо трансформація явно вимкнена - повертаємо опції без змін
        if (settings?.disableRequestTransform) {
            return options
        }

        const serviceConfig = configManager.get('ServiceConfig') as any

        if (!settings?.requestTransformer && serviceConfig?.restClient?.requestTransformer) {
            settings.requestTransformer = serviceConfig.restClient.requestTransformer
        }

        if (!settings?.requestTransformer) {
            return options
        }

        let transformer: RequestTransformer

        if (typeof settings.requestTransformer === 'function') {
            transformer = settings.requestTransformer
        } else {
            const transformerName = settings.requestTransformer as keyof typeof RequestTransformers
            transformer = RequestTransformers[transformerName] || RequestTransformers.none
        }

        return transformer(options)
    }

    /**
     * Підготовлює опції з трансформацією відповіді
     *
     * @param options - базові опції запиту
     * @param settings - налаштування з можливістю вказати трансформер
     * @returns опції з доданим afterResponse хуком
     *
     * @description
     * Трансформери відповіді використовуються для:
     * - Розпакування вкладених структур (hydra:member, data тощо)
     * - Нормалізації форматів відповідей від різних API
     * - Перетворення даних у єдиний формат
     */
    protected prepareOptionsWithTransformer(options?: Options, settings: TSettings = {} as TSettings): Options {
        const opts = options ?? {}

        if (settings?.disableResponseTransform) {
            return opts
        }

        const serviceConfig = configManager.get('ServiceConfig') as any

        if (!settings?.responseTransformer && serviceConfig?.restClient?.responseTransformer) {
            settings.responseTransformer = serviceConfig.restClient.responseTransformer
        }

        if (!settings?.responseTransformer) {
            return opts
        }

        if (!opts.hooks) {
            opts.hooks = {}
        }

        if (!Array.isArray(opts.hooks.afterResponse)) {
            opts.hooks.afterResponse = []
        }

        let transformer: CollectionTransformer

        if (typeof settings.responseTransformer === 'function') {
            transformer = settings.responseTransformer
        } else {
            const transformerName = settings.responseTransformer as keyof typeof CollectionTransformers
            transformer = CollectionTransformers[transformerName] || CollectionTransformers.none
        }

        opts.hooks.afterResponse.push(async (_request: Request, _options: unknown, response: Response) => {
            if (response.ok) {
                // Статуси 204 (No Content) та 205 (Reset Content) не мають тіла відповіді
                // Парсинг JSON для них викличе помилку, тому повертаємо відповідь як є
                if (response.status === 204 || response.status === 205) {
                    return response
                }

                return response.json().then((data) => {
                    const transformed = transformer(data)

                    let finalData: unknown

                    if (transformed && typeof transformed === 'object' && 'items' in transformed) {
                        finalData = transformed
                    } else {
                        finalData = transformed
                    }

                    const headers = new Headers(response.headers)
                    headers.set('Content-Type', 'application/json')

                    return new Response(JSON.stringify(finalData), {
                        status: response.status,
                        statusText: response.statusText,
                        headers: headers,
                    })
                })
            }

            return response
        })

        return opts
    }

    /**
     * Заповнює опції стандартними headers та hooks
     *
     * @param options - базові опції запиту
     * @param settings - налаштування клієнта
     * @returns опції з доданими headers та hooks
     *
     * @description
     * Додає:
     * - Content-Type (якщо не вказано інше)
     * - X-LOCALE (з конфігурації)
     * - Authorization Bearer токен (для не публічних запитів)
     * - Профілювання запитів (у dev режимі)
     * - Автоматичне оновлення токенів при 401
     */
    protected fillOptions(options: Options, settings: TSettings): Options {
        const opts = options ?? {}
        const setts = settings ?? ({} as TSettings)

        opts.hooks = opts.hooks ?? {}

        if (!Array.isArray(opts.hooks.afterResponse)) {
            opts.hooks.afterResponse = []
        }

        if (!Array.isArray(opts.hooks.beforeRequest)) {
            opts.hooks.beforeRequest = []
        }

        if (!opts.headers) {
            opts.headers = {}
        }

        const headers = opts.headers as Record<string, string>

        // Content-Type за замовчуванням
        if (headers['Content-Type'] === undefined && !setts?.isNotHeaderAddContentType) {
            headers['Content-Type'] = 'application/ld+json'
        }

        // Додаємо локаль
        const appConfig = configManager.get('AppConfig') as any
        if (headers['X-LOCALE'] === undefined && appConfig?.currentLocale) {
            headers['X-LOCALE'] = appConfig.currentLocale
        }

        // Профілювання запитів (перший хук для точності вимірювань)
        if (requestProfiler.enabled && setts?.profileId) {
            opts.hooks.afterResponse.unshift(async (_request: Request, _options: unknown, response: Response) => {
                const clonedResponse = response.clone()

                let responseData: unknown
                try {
                    const contentType = clonedResponse.headers.get('content-type')
                    if (contentType?.includes('application/json') || contentType?.includes('application/ld+json')) {
                        responseData = await clonedResponse.json()
                    } else if (contentType?.includes('text')) {
                        responseData = await clonedResponse.text()
                    }
                } catch (error) {
                    // Ігноруємо помилки парсингу
                }

                requestProfiler.endRequest(setts.profileId!, response, responseData)
            })
        }

        // Для публічних запитів не додаємо авторизацію
        if (setts?.isPublic) {
            return opts
        }

        // Автоматичне оновлення токена при 401 через beforeRetry hook
        // beforeRetry викликається автоматично при помилках, якщо налаштовано retry
        opts.retry = opts.retry ?? {
            limit: 1, // Одна спроба оновити токен
            methods: ['get', 'post', 'put', 'patch', 'delete'],
            statusCodes: [401], // Тільки для 401
        }

        opts.hooks.beforeRetry = opts.hooks.beforeRetry ?? []
        opts.hooks.beforeRetry.push(async ({ request, error, retryCount }: { request: Request; error: Error; retryCount: number }) => {
            // Перевіряємо що це 401 помилка
            const httpError = error as HTTPError
            if (httpError.response?.status === 401 && retryCount === 1) {
                const refreshSuccess = await userManager.refreshAccessToken()

                if (refreshSuccess) {
                    // Оновлюємо токен у headers для повторного запиту
                    request.headers.set('Authorization', `Bearer ${userManager.token}`)
                } else {
                    // Якщо оновлення не вдалося - скасовуємо повтор
                    throw error
                }
            }
        })

        // Додаємо Bearer токен перед кожним запитом (тільки якщо токен існує)
        const setHeadersBearerToken = async (request: Request) => {
            await userManager.waitForHydration()
            if (!request.headers.has('Authorization') && userManager.token) {
                request.headers.set('Authorization', `Bearer ${userManager.token}`)
            }
        }

        opts.hooks.beforeRequest.push(setHeadersBearerToken)

        return opts
    }

    /**
     * Отримує повний URL з урахуванням backendUrl
     *
     * @param url - відносний або абсолютний URL
     * @param settings - налаштування з можливим перевизначенням backendUrl
     * @returns повний URL
     */
    protected getFullUrl(url: string, settings?: TSettings): string {
        const backendUrl = settings?.backendUrl ?? this.backendUrl ?? ''
        return `${backendUrl}${url}`
    }

    /**
     * Отримує ім'я HTTP методу для профілювання
     *
     * @param method - функція методу з Ky
     * @returns назва методу у верхньому регістрі
     */
    protected getMethodName(method: Function): string {
        const methodStr = method.toString()

        if (methodStr.includes('get')) return 'GET'
        if (methodStr.includes('post')) return 'POST'
        if (methodStr.includes('put')) return 'PUT'
        if (methodStr.includes('patch')) return 'PATCH'
        if (methodStr.includes('delete')) return 'DELETE'

        return 'UNKNOWN'
    }

    /**
     * Початок профілювання запиту
     *
     * @param method - назва HTTP методу
     * @param url - повний URL запиту
     * @param options - опції запиту
     * @param settings - налаштування клієнта
     * @returns ID профілю або undefined
     */
    protected startProfiling(method: string, url: string, options: Options, settings: TSettings): string | undefined {
        if (!requestProfiler.enabled) {
            return undefined
        }

        return requestProfiler.startRequest(method, url, options, settings)
    }
}
